import React, { useState, useEffect } from 'react';
import { Calendar, Clock } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DateTimePickerProps {
  value: string;
  onChange: (value: string) => void;
  min?: string;
  className?: string;
}

interface BookedSlot {
  time: string;
  patientName: string;
}

export default function DateTimePicker({ value, onChange, min, className = '' }: DateTimePickerProps) {
  const [date, setDate] = useState('');
  const [time, setTime] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [bookedSlots, setBookedSlots] = useState<BookedSlot[]>([]);
  const [showTimeDropdown, setShowTimeDropdown] = useState(false);

  // Available time slots between 19:00 and 20:15
  const availableSlots = [
    '19:00',
    '19:15',
    '19:30',
    '19:45',
    '20:00',
    '20:15'
  ];

  useEffect(() => {
    if (value) {
      const dateObj = new Date(value);
      setDate(dateObj.toISOString().split('T')[0]);
      setTime(dateObj.toTimeString().slice(0, 5));
    }
  }, [value]);

  useEffect(() => {
    if (date) {
      fetchBookedSlots(date);
    }
  }, [date]);

  const fetchBookedSlots = async (selectedDate: string) => {
    try {
      const { data: appointments, error: fetchError } = await supabase
        .from('profiles')
        .select('planned_date, prenume, nume')
        .eq('consultation_status', 'planned')
        .gte('planned_date', `${selectedDate}T00:00:00`)
        .lte('planned_date', `${selectedDate}T23:59:59`);

      if (fetchError) throw fetchError;

      const slots: BookedSlot[] = appointments?.map(appointment => ({
        time: new Date(appointment.planned_date).toTimeString().slice(0, 5),
        patientName: `${appointment.prenume} ${appointment.nume}`
      })) || [];

      setBookedSlots(slots);

      // If current time is booked, clear it
      if (time && slots.some(slot => slot.time === time)) {
        setTime('');
        setError('Acest interval orar este deja rezervat');
        onChange('');
      }
    } catch (err) {
      console.error('Error fetching booked slots:', err);
    }
  };

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newDate = e.target.value;
    const selectedDate = new Date(newDate);
    
    // Check if selected date is a Wednesday (3)
    if (selectedDate.getDay() !== 3) {
      setError('Programările sunt disponibile doar miercurea');
      setDate('');
      setTime('');
      onChange('');
      return;
    }

    setError(null);
    setDate(newDate);
    setTime(''); // Clear time when date changes
    onChange(''); // Clear the full datetime when date changes
  };

  const handleTimeSelect = (selectedTime: string) => {
    // Check if the time slot is already booked
    if (bookedSlots.some(slot => slot.time === selectedTime)) {
      setError('Acest interval orar este deja rezervat');
      return;
    }

    setError(null);
    setTime(selectedTime);
    updateDateTime(date, selectedTime);
    setShowTimeDropdown(false);
  };

  const updateDateTime = (newDate: string, newTime: string) => {
    if (newDate && newTime) {
      const selectedDateTime = new Date(`${newDate}T${newTime}`);
      const now = new Date();

      // Clear any previous error
      setError(null);

      // Check if the selected date/time is in the past
      if (selectedDateTime < now) {
        setError('Nu puteți selecta o dată din trecut');
        return;
      }

      // Check if selected date is a Wednesday
      if (selectedDateTime.getDay() !== 3) {
        setError('Programările sunt disponibile doar miercurea');
        return;
      }

      // Check if the time slot is already booked
      if (bookedSlots.some(slot => slot.time === newTime)) {
        setError('Acest interval orar este deja rezervat');
        return;
      }

      onChange(selectedDateTime.toISOString());
    }
  };

  // Get current date and time for min values
  const now = new Date();
  const currentDate = now.toISOString().split('T')[0];

  return (
    <div className="space-y-2">
      <div className={`flex gap-4 ${className}`}>
        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            <Calendar className="w-5 h-5" />
          </div>
          <input
            type="date"
            value={date}
            min={currentDate}
            onChange={handleDateChange}
            className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-[#024994] focus:border-[#024994]"
          />
        </div>

        <div className="relative flex-1">
          <div className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500">
            <Clock className="w-5 h-5" />
          </div>
          <button
            type="button"
            onClick={() => date && setShowTimeDropdown(!showTimeDropdown)}
            className={`w-full pl-10 pr-3 py-2 text-left border border-gray-300 rounded-md focus:ring-[#024994] focus:border-[#024994] bg-white ${!date ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {time || 'Selectează ora'}
          </button>

          {showTimeDropdown && date && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-gray-300 rounded-md shadow-lg">
              <div className="max-h-48 overflow-y-auto">
                {availableSlots.map((slot) => {
                  const isBooked = bookedSlots.find(booked => booked.time === slot);
                  return (
                    <div
                      key={slot}
                      className={`px-4 py-2 ${
                        isBooked 
                          ? 'bg-gray-50 cursor-not-allowed' 
                          : 'cursor-pointer hover:bg-gray-100'
                      }`}
                      onClick={() => !isBooked && handleTimeSelect(slot)}
                    >
                      <div className="flex justify-between items-center">
                        <span className={isBooked ? 'text-gray-400' : ''}>
                          {slot}
                        </span>
                        {isBooked && (
                          <span className="text-sm text-gray-500">
                            {isBooked.patientName}
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      </div>
      {error && (
        <p className="text-red-500 text-sm">{error}</p>
      )}
      <p className="text-sm text-gray-500">
        * Programările sunt disponibile doar miercurea între orele 19:00-20:15
      </p>
    </div>
  );
}